import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ActionButton } from '@/shared/ui/action-button';
import type { AttachmentType } from '@/entities/consignments';

import { useAttachmentUpload } from '../model/use-attachment-upload';
import { AttachmentTypeSelect } from './attachment-type-select';
import { FileUploadSection } from './file-upload-section';
import { CommentInput } from './comment-input';

interface AttachmentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddAttachment: (file: File, attachmentType: AttachmentType, comment?: string) => void;
  onSuccess?: () => void;
  /** Restricts the type list — receipts reject `GO`. */
  allowedTypes?: AttachmentType[];
}

export function AttachmentUploadModal({
  isOpen,
  onClose,
  onAddAttachment,
  onSuccess,
  allowedTypes
}: AttachmentUploadModalProps) {
  const { t } = useTranslation();
  const {
    formData,
    handleFileSelect,
    handleRemoveFile,
    handleAttachmentTypeChange,
    handleCommentChange,
    handleSubmit,
    resetForm
  } = useAttachmentUpload({
    onAddAttachment,
    onSuccess
  });

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const isSubmitDisabled = !formData.file || !formData.attachmentType;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('newcmr.add.attachment.label')}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <AttachmentTypeSelect
            value={formData.attachmentType}
            onChange={handleAttachmentTypeChange}
            allowedTypes={allowedTypes}
          />
          <FileUploadSection file={formData.file} onFileSelect={handleFileSelect} onFileRemove={handleRemoveFile} />
          <CommentInput value={formData.comment || ''} onChange={handleCommentChange} />
        </div>

        <DialogFooter>
          <ActionButton type="button" intent="dismiss" onClick={handleClose}>
            {t('gen.cancel.label')}
          </ActionButton>
          <ActionButton intent="primary" type="button" onClick={handleSubmit} disabled={isSubmitDisabled}>
            {t('gen.upload.label')}
          </ActionButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
